<template>
  <app-page :title="$t('@insurance/pageTitle', { id: contract.internalId })">
    <errors-list :errorMessage="errorMessage" />
    <v-flex shrink slot="actions">
      <v-btn icon :disabled="!pdfReady" :href="`/api/insurances/${contract.id}/pdf/policy`">
        <v-icon>receipt</v-icon>
      </v-btn>
    </v-flex>

    <v-container grid-list-md>
      <v-layout row wrap class="timeline">
        <v-flex xs3 style="padding-left: 0">
          <v-list two-line style="padding: 0">
            <v-list-tile class="timeline-box">
              <v-list-tile-avatar>
                <v-icon size="36" color="green">schedule</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content class="timeline-text">
                <div style="font-size: 13px">
                  <strong>Försäkring skapad:</strong>
                </div>
                <div>
                  {{ contractCreatedAt }}
                </div>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-flex>
        <v-flex xs3>
          <v-list two-line style="padding: 0">
            <v-list-tile class="timeline-box">
              <v-list-tile-avatar>
                <v-icon size="36" color="grey">schedule</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content class="timeline-text">
                <div><strong>Anbud skapad:</strong></div>
                <div>{{ offerCreatedAt }}</div>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-flex>
        <v-flex xs3>
          <v-list two-line style="padding: 0">
            <v-list-tile class="timeline-box">
              <v-list-tile-avatar>
                <v-icon size="36" color="blue">schedule</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content class="timeline-text">
                <div><strong>Anbud accepterad:</strong></div>
                <div>{{ acceptedAt }}</div>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-flex>
        <v-flex xs3 style="padding-right: 0">
          <v-list two-line style="padding: 0">
            <v-list-tile style="background: white; border: 1px solid #ccc">
              <v-list-tile-avatar>
                <v-icon size="36" color="red">schedule</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content class="timeline-text">
                <div><strong>Avslutad:</strong></div>
                <div>{{ isTerminated ? terminationDate : '-' }}</div>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-flex>
      </v-layout>
    </v-container>

    <v-layout row wrap>
      <v-flex md4>
        <v-list dense>
          <v-subheader>
            <span>{{ $t('@insurance/generalInfo/title') }}</span>
          </v-subheader>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@insurance/generalinfo/statusLabel') }}
              :
            </v-list-tile-content>
            <v-list-tile-content :class="contractStatusMessage">
              {{ contractStatus }}
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@insurance/generalinfo/signedAtLabel') }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">{{ acceptedAt }}</v-list-tile-content>
          </v-list-tile>

          <v-subheader>{{ $t('@insurance/productInfo/title') }}</v-subheader>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@insurance/productInfo/productNameLabel') }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">{{ contract.product.name }}</v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@insurance/productInfo/createdbylabel') }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">{{ createdBy }}</v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@insurance/productInfo/createdsourcelabel') }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">{{ createdSource }}</v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@insurance/productInfo/currentballancelabel') }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              {{ currentBalance }}
              kr
            </v-list-tile-content>
          </v-list-tile>

          <template v-if="isTerminated">
            <v-subheader>{{ $t('@insurance/productInfo/terminationTitle') }}</v-subheader>

            <v-list-tile>
              <v-list-tile-content>
                {{ $t('@insurance/productInfo/terminationReasonLabel') }}:
              </v-list-tile-content>
              <v-list-tile-content class="align-end">{{ terminationReason }}</v-list-tile-content>
            </v-list-tile>
            <v-list-tile>
              <v-list-tile-content>
                {{ $t('@insurance/productInfo/terminationDateLabel') }}:
              </v-list-tile-content>
              <v-list-tile-content class="align-end">{{ terminationDate }}</v-list-tile-content>
            </v-list-tile>
            <v-list-tile>
              <v-list-tile-content>
                {{ $t('@insurance/productInfo/terminatedByLabel') }}:
              </v-list-tile-content>
              <v-list-tile-content class="align-end">{{ terminatedBy }}</v-list-tile-content>
            </v-list-tile>
          </template>

          <template v-if="hasActiveCampaign">
            <v-subheader>{{ $t('@insurance/campaigns/title') }}</v-subheader>
            <v-list-tile mb-2>
              <v-list-tile-content>
                <div>
                  <span class="mr-1">{{ $t('@insurance/campaigns/hasActiveCampaign') }}</span>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon :style="{ 'vertical-align': 'text-bottom' }" v-on="on" small>
                        info
                      </v-icon>
                    </template>
                    <span>{{ $t('@insurance/campaigns/hasActiveCampaign/description') }}</span>
                  </v-tooltip>
                </div>
              </v-list-tile-content>
              <v-list-tile-content class="align-end">
                <div class="space-around">
                  <v-icon :style="{ 'vertical-align': 'bottom' }" color="green">
                    check_circle
                  </v-icon>
                  <span inline-block class="ml-1">
                    {{ $t('@insurance/campaigns/hasActiveCampaign/yes') }}
                  </span>
                </div>
              </v-list-tile-content>
            </v-list-tile>
          </template>

          <template
            v-if="
              contract.product.productGroupId === productGroups.TF &&
                contract.originalCoveredAmount &&
                contract.originalCoveredAmount !== contract.productVariation.coveredAmount
            "
          >
            <v-list-tile>
              <v-list-tile-content>
                {{ $t('@insurance/premium/originalCoveredAmount') }}:
              </v-list-tile-content>
              <v-list-tile-content class="align-end">
                {{ contract.originalCoveredAmount ? `${contract.originalCoveredAmount} kr` : '-' }}
              </v-list-tile-content>
            </v-list-tile>

            <v-list-tile>
              <v-list-tile-content>
                {{ $t('@insurance/premium/originalPremium') }}:
              </v-list-tile-content>
              <v-list-tile-content class="align-end">
                {{ contract.originalPremium ? `${contract.originalPremium} kr` : '-' }}
              </v-list-tile-content>
            </v-list-tile>
          </template>
        </v-list>
      </v-flex>

      <v-flex md4>
        <v-list dense>
          <v-subheader>{{ $t('@insurance/premium/title') }}</v-subheader>

          <v-list-tile v-if="contract.product.productGroupId === productGroups.TF">
            <v-list-tile-content>
              {{ $t('@insurance/premium/premiumCoverage') }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              {{ contract.productVariation.coveredAmount }} kr
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>{{ $t('@insurance/premium/monthlyCost') }}:</v-list-tile-content>
            <v-list-tile-content class="align-end">
              {{ contract.periodCost }} kr
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>{{ $t('@insurance/premium/startDate') }}:</v-list-tile-content>
            <v-list-tile-content class="align-end">{{ premiumStartDate }}</v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@insurance/premium/lastAdjustmentBy') }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">{{ lastAdjustmentBy }}</v-list-tile-content>
          </v-list-tile>

          <template v-if="contract.product.productGroupId === productGroups.TF">
            <v-subheader>
              <span>{{ $t('@insurance/premium/qualificationPeriod') }}</span>
            </v-subheader>

            <v-list-tile>
              <v-list-tile-content>
                {{ $t('@insurance/premium/qualificationPeriodInabilityToWork') }} (30d):
              </v-list-tile-content>
              <v-list-tile-content class="align-end">
                {{ inabilityToWorkCoverageStartDate }}
              </v-list-tile-content>
            </v-list-tile>

            <v-list-tile>
              <v-list-tile-content>
                {{ $t('@insurance/premium/qualificationPeriodInvoluntaryUnemployment') }} (90d):
              </v-list-tile-content>
              <v-list-tile-content class="align-end">
                {{ involuntaryUnemploymentCoverageStartDate }}
              </v-list-tile-content>
            </v-list-tile>
          </template>
        </v-list>
      </v-flex>

      <v-flex md4>
        <v-list dense>
          <v-subheader>{{ $t('@insurance/bankDetails/title') }}</v-subheader>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@insurance/bankDetails/statusLabel') }}:
            </v-list-tile-content>
            <v-list-tile-content :class="statusBankDetailsMessage">
              {{ bankDetailsStatus }}
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@insurance/bankDetails/clearingLabel') }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              {{ contract.bankDetails.clearingNumber || '—' }}
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@insurance/bankDetails/bankNumberLabel') }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              {{ contract.bankDetails.bankNumber || '—' }}
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@insurance/bankDetails/bankNameLabel') }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              {{ contract.bankDetails.bankName || '—' }}
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@insurance/bankDetails/bankDetailsIdLabel') }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              {{ contract.bankDetails.id || '—' }}
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>
              {{
                bankDetailsWereUpdated
                  ? $t('@insurance/bankDetails/bankDetailsUpdatedAtLabel')
                  : $t('@insurance/bankDetails/bankDetailsCreatedAtLabel')
              }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              {{ bankDetailsFormattedDate }}
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>
              {{
                bankDetailsStatusWasUpdated
                  ? $t('@insurance/bankDetails/bankDetailsStatusUpdatedAtLabel')
                  : $t('@insurance/bankDetails/bankDetailsStatusCreatedAtLabel')
              }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              {{ bankDetailsStatusFormattedDate }}
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content style="width: 250px">
              {{ $t('@insurance/bankDetails/bgcCommentCodeLabel') }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              <v-tooltip bottom :disabled="!contract.bankDetailsStatus.bgcCommentCode">
                <template v-slot:activator="{ on }">
                  <span
                    v-on="on"
                    v-bind:class="{ hoverable: contract.bankDetailsStatus.bgcCommentCode }"
                    style="padding-right: 0; text-align: right; line-height: 14px"
                  >
                    {{
                      contract.bankDetailsStatus.bgcCommentCode
                        ? getMandateCommentCode(contract.bankDetailsStatus.bgcCommentCode)
                        : '—'
                    }}
                  </span>
                </template>
                <span>{{ contract.bankDetailsStatus.bgcCommentCode }}</span>
              </v-tooltip>
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content style="width: 250px">
              {{ $t('@insurance/bankDetails/bgcInformationCodeLabel') }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              <v-tooltip bottom :disabled="!contract.bankDetailsStatus.bgcInformationCode">
                <template v-slot:activator="{ on }">
                  <span
                    v-on="on"
                    v-bind:class="{ hoverable: contract.bankDetailsStatus.bgcInformationCode }"
                    style="padding-right: 0; text-align: right; line-height: 16px"
                  >
                    {{
                      contract.bankDetailsStatus.bgcInformationCode
                        ? getMandateInformationCode(contract.bankDetailsStatus.bgcInformationCode)
                        : '—'
                    }}
                  </span>
                </template>
                <span>
                  {{ contract.bankDetailsStatus.bgcInformationCode }}
                </span>
              </v-tooltip>
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile>
            <v-list-tile-content>
              {{ $t('@insurance/bankDetails/bankDetailsAddedByEmployeeLabel') }}:
            </v-list-tile-content>
            <v-list-tile-content class="align-end">
              {{ bankDetailsAddedBy }}
            </v-list-tile-content>
          </v-list-tile>
        </v-list>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex xs12>
        <v-card class="mb-3" flat>
          <v-card-title primary-title>
            <v-icon class="mr-2">comment</v-icon>
            <h4>Kommentarer</h4>
          </v-card-title>

          <v-divider />
          <div style="padding: 10px 16px">
            <table v-if="contract.comments.length" class="comments-table">
              <thead>
                <tr>
                  <th align="left">Datum</th>
                  <th align="left">Skapad av</th>
                  <th align="left">Kommentar</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="comment in contract.comments" :key="comment.id">
                  <td valign="top" style="width: 200px">
                    {{ defaultDateTimeFormatter(comment.createdAt) }}
                  </td>
                  <td valign="top" style="width: 200px">
                    {{ `${comment.createdBy.firstName} ${comment.createdBy.lastName}` }}
                  </td>
                  <td valign="top" style="white-space: pre-wrap">{{ comment.comment }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else>Inga kommentarer</div>
          </div>
          <v-divider />
          <v-btn @click="showAddCommentDialog = true">Lägg till kommentar</v-btn>
        </v-card>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex xs12>
        <v-card class="mb-3" flat v-if="backgroundErrors.length">
          <v-card-title>
            <v-icon class="mr-2">error</v-icon>
            <h4>Errors</h4>
          </v-card-title>

          <v-divider />
          <background-errors-list :loading="contractLoading" :items="backgroundErrors" />
        </v-card>

        <v-card class="mb-3" flat>
          <v-card-title>
            <v-icon class="mr-2">supervised_user_circle</v-icon>
            <h4>{{ $t('@insurance/participantsListTitle') }}</h4>
          </v-card-title>

          <v-divider />
          <customer-list
            :loading="contractLoading"
            :items="contract.customers"
            :internalId="contract.internalId"
            @select="onCustomerClick"
          />
        </v-card>

        <v-tabs :key="contract.id" v-model="activeTab" v-if="contract.id" grow>
          <v-tab key="audit-trail" class="grey lighten-4">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="mr-2" v-on="on">history</v-icon>
              </template>
              <span>{{ $t('@insurance/auditTrailTabTitle') }}</span>
            </v-tooltip>
          </v-tab>

          <v-tab key="debt" class="grey lighten-4">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="mr-2" v-on="on">schedule</v-icon>
              </template>
              <span>{{ $t('@insurance/debtsTabTitle') }}</span>
            </v-tooltip>
          </v-tab>

          <v-tab key="transaction" class="grey lighten-4">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="mr-2" v-on="on">account_balance_wallet</v-icon>
              </template>
              <span>{{ $t('@insurance/transActionsTabTitle') }}</span>
            </v-tooltip>
          </v-tab>

          <v-tab key="invoice" class="grey lighten-4">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="mr-2" v-on="on">attach_money</v-icon>
              </template>
              <span>{{ $t('@insurance/invoicesTabTitle') }}</span>
            </v-tooltip>
          </v-tab>
          <v-tab key="bgc-audit-trail" class="grey lighten-4">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="mr-2" v-on="on">update</v-icon>
              </template>
              <span>{{ $t('@insurance/bgcAuditTrailTabTitle') }}</span>
            </v-tooltip>
          </v-tab>

          <v-tab key="recommendation" class="grey lighten-4">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="mr-2" v-on="on">manage_history</v-icon>
              </template>
              <span>{{ $t('@insurance/recommendationTabTitle') }}</span>
            </v-tooltip>
          </v-tab>

          <v-tab key="winback" class="grey lighten-4">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="mr-2" v-on="on">paid</v-icon>
              </template>
              <span class="tooltip">{{ $t('@insurance/winback') }}</span>
            </v-tooltip>
          </v-tab>

          <v-tab key="campaigns" class="grey lighten-4">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="mr-2" v-on="on">discount</v-icon>
              </template>
              <span class="tooltip">{{ $t('@insurance/campaigns') }}</span>
            </v-tooltip>
          </v-tab>

          <v-tab-item key="audit-trail-content">
            <v-divider />
            <audit-trail-list
              :filters="{
                contractId: contract.id,
                from: contract.created,
                invoiceIds,
                offerId: contract.offer ? contract.offer.id : '',
              }"
            />
          </v-tab-item>

          <v-tab-item key="debt-content">
            <v-divider />
            <debt-list :filters="{ contractId: contract.id }" />
          </v-tab-item>

          <v-tab-item key="transaction-content">
            <v-divider />
            <v-card flat>
              <transaction-list :filters="{ contractId: contract.id }" />
            </v-card>
          </v-tab-item>

          <v-tab-item key="invoice-content">
            <v-divider />
            <invoice-list
              :key="contract.id"
              :filters="{ contractId: contract.id }"
              @data-fetched="fetchedInvoices"
              @invoice-details-updated="fetchBalance()"
            />
          </v-tab-item>

          <v-tab-item key="bgc-audit-trail-content">
            <v-divider />
            <bgc-audit-trail-list
              :filters="{
                from: contract.created,
                bankDetailsStatusId: contract.bankDetailsStatus.id,
                bankDetailsId:
                  contract.bankDetails.id === '-' ? undefined : contract.bankDetails.id,
              }"
            />
          </v-tab-item>

          <v-tab-item key="recommendation-content">
            <v-divider />
            <recommendation-list :recommendation="contract.recommendation" />
          </v-tab-item>

          <v-tab-item key="winback-content">
            <v-divider />
            <winback-list :contract="contract" />
          </v-tab-item>

          <v-tab-item key="campaigns-content">
            <v-divider />
            <campaigns-list :contract="contract" />
          </v-tab-item>
        </v-tabs>
      </v-flex>
    </v-layout>

    <v-speed-dial
      :value="true"
      top
      right
      absolute
      :style="insetFab ? { top: '32px', right: '12px' } : { top: '74px', right: '-120px' }"
      :direction="insetFab ? 'left' : 'bottom'"
      transition="slide-y-reverse-transition"
    >
      <!-- Workaround for INSURANCE-792 -->
      <template v-if="showActionsFabButtons">
        <div
          class="text-center align-center"
          :class="insetFab ? 'd-flex' : 'flex-column'"
          @click.stop
        >
          <div class="d-flex justify-start flex-row align-center action-btn" v-if="!isTerminated">
            <v-btn
              fab
              dark
              small
              color="orange"
              @click.stop="(bankDetailsDialog = true), (fab = false)"
            >
              <v-icon>account_balance</v-icon>
            </v-btn>
            <div v-if="showFabTooltips" @click.stop>
              <span class="tooltip">
                {{ $t('@insurance/updateBankDetailsActionLabel') }}
              </span>
            </div>
          </div>

          <div
            class="d-flex justify-start flex-row align-center action-btn"
            v-if="
              contract.product.supportsLoweringPremium &&
                !isTerminated &&
                !hideFinancialOperationBtn &&
                contract.productVariation
            "
          >
            <v-btn
              fab
              dark
              small
              color="indigo"
              @click.stop="(premiumDialog = true), (fab = false)"
            >
              <v-icon>star_half</v-icon>
            </v-btn>
            <div v-if="showFabTooltips" @click.stop>
              <span class="tooltip">{{ $t('@insurance/changePremiumActionLabel') }}</span>
            </div>
          </div>

          <div
            class="d-flex justify-start flex-row align-center action-btn"
            v-if="!hideFinancialOperationBtn && !isTerminated"
          >
            <v-btn
              fab
              dark
              small
              color="red darken-1"
              @click.stop="(balanceCorrectionDialog = true), (fab = false)"
            >
              <v-icon>attach_money</v-icon>
            </v-btn>
            <div v-if="showFabTooltips" @click.stop>
              <span class="tooltip">{{ $t('@insurance/ballanceCorrectionActionLabel') }}</span>
            </div>
          </div>

          <div class="d-flex justify-start flex-row align-center action-btn" v-if="!isTerminated">
            <v-btn
              fab
              dark
              small
              color="red darken-1"
              @click.stop="(terminateDialog = true), (fab = false)"
            >
              <v-icon>delete</v-icon>
            </v-btn>
            <div v-if="showFabTooltips" @click.stop>
              <span class="tooltip">{{ $t('@insurance/terminateActionLabel') }}</span>
            </div>
          </div>

          <div
            class="d-flex justify-start flex-row align-center action-btn"
            v-if="contract.status === 0"
          >
            <v-btn fab dark small color="green darken-1" @click.stop="manualReminderDialog = true">
              <v-icon>add_alert</v-icon>
            </v-btn>
            <div v-if="showFabTooltips" @click.stop>
              <span class="tooltip">{{ $t('@insurance/bankDetails/manualReminder') }}</span>
            </div>
          </div>

          <div class="d-flex justify-start flex-row align-center action-btn" v-if="!isTerminated">
            <v-btn fab dark small color="blue" @click.stop="winBackDialog = true">
              <v-icon>paid</v-icon>
            </v-btn>
            <div v-if="showFabTooltips" @click.stop>
              <span class="tooltip">{{ $t('@insurance/winback') }}</span>
            </div>
          </div>

          <div
            class="d-flex justify-start flex-row align-center action-btn"
            v-if="showUnterminationFabButton"
          >
            <v-btn
              fab
              dark
              small
              :color="canBeUnterminated ? 'red darken-1' : 'grey'"
              @click.stop="onClickShowUnterminationDialog"
            >
              <v-icon>attach_money</v-icon>
            </v-btn>
            <div v-if="showFabTooltips" @click.stop>
              <span class="tooltip">
                {{ $t('@insurance/unterminateActionLabel') }}
              </span>
            </div>
          </div>

          <div class="d-flex justify-start flex-row align-center action-btn" v-if="isTerminated">
            <v-btn fab dark small color="blue" @click.stop="repaymentDialog = true">
              <v-icon>attach_money</v-icon>
            </v-btn>
            <div v-if="showFabTooltips" @click.stop>
              <span class="tooltip">{{ $t('@insurance/repayment') }}</span>
            </div>
          </div>
        </div>
      </template>
    </v-speed-dial>

    <v-dialog
      max-width="480px"
      v-model="premiumDialog"
      v-if="
        (contract.product.supportsLoweringPremium || contract.product.supportsRaisingPremium) &&
          contract.productVariation
      "
    >
      <premium-form
        :contract="contract"
        @dismiss="premiumDialog = false"
        @submitted="fetchContract()"
      />
    </v-dialog>

    <v-dialog v-model="bankDetailsDialog" persistent lazy max-width="390px">
      <bank-details-form
        :contract="contract"
        @dismiss="bankDetailsDialog = false"
        @submitted="fetchContract()"
      />
    </v-dialog>

    <v-dialog v-model="terminateDialog" persistent lazy width="390px">
      <terminate-form
        :contract="contract"
        @submitted="fetchContract()"
        @dismiss="terminateDialog = false"
      />
    </v-dialog>

    <v-dialog v-model="balanceCorrectionDialog" persistent lazy width="480px">
      <balance-correction-form
        v-if="balanceCorrectionDialog"
        :contract="contract"
        @submitted="fetchBalance()"
        @dismiss="balanceCorrectionDialog = false"
      />
    </v-dialog>

    <v-dialog v-model="manualReminderDialog" lazy max-width="480px">
      <manual-reminder-form :contract="contract" @dismiss="manualReminderDialog = false" />
    </v-dialog>
    <v-dialog v-model="winBackDialog" persistent lazy width="540px" scrollable>
      <winback-form v-if="winBackDialog" :contract="contract" @dismiss="winBackDialog = false" />
    </v-dialog>
    <v-dialog v-model="repaymentDialog" persistent lazy width="850px" scrollable>
      <repayment-form
        v-if="repaymentDialog"
        :contract="contract"
        @dismiss="repaymentDialog = false"
      />
    </v-dialog>

    <contract-add-comment-dialog v-model="showAddCommentDialog" @save="onAddComment" />

    <v-dialog v-model="shouldShowUnterminationDialog" persistent lazy width="490px">
      <v-card>
        <v-card-title class="headline">
          {{ $t('@insurance/unterminateTitle') }}
        </v-card-title>
        <v-card-text>
          {{ $t('@insurance/unterminateBody') }}
        </v-card-text>

        <errors-list :errorMessage="errorMessageUnterminate" />

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" flat @click.stop="shouldShowUnterminationDialog = false">
            {{ $t('@insurance/unterminateCancel') }}
          </v-btn>

          <v-btn
            color="green darken-1"
            flat
            @click.stop="tryUnterminate()"
            :disabled="!canBeUnterminated"
          >
            {{ $t('@insurance/unterminateYes') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </app-page>
</template>

<script>
import moment from 'moment';
import ContractAddCommentDialog from '../../components/ContractAddCommentDialog.vue';
import ErrorsList from '../../components/ErrorsList';
import { getErrorMessage } from '../../errorHandlers';
import { defaultDateTimeFormatter, isoDateFormatter } from '../../utils';
import DebtList from '../Debts/DebtList';
import InvoiceList from '../Invoices/InvoiceList';
import AuditTrailList from './AuditTrailList';
import BackgroundErrorsList from './BackgroundErrorsList';
import BalanceCorrectionForm from './BalanceCorrectionForm';
import BankDetailsForm from './BankDetailsForm';
import BgcAuditTrailList from './BgcAuditTrailList';
import CampaignsList from './CampaignsList';
import CustomerList from './CustomerList';
import ManualReminderForm from './ManualReminderForm';
import PremiumForm from './PremiumForm';
import RecommendationList from './RecomendationList';
import RepaymentForm from './RepaymentForm';
import TerminateForm from './TerminateForm';
import TransactionList from './TransactionList';
import WinbackForm from './WinbackForm';
import WinbackList from './WinbackList';

import { mapGetters } from 'vuex';
import {
  ContractStatusEnum,
  bankDetailsStatus,
  contractStatusLabel,
  getMandateCommentCode,
  getMandateInformationCode,
} from '../../constants';
import {
  BALANCE_CORRECTION_CREATED,
  CONTACT_PERSON_CHANGED,
  INSURANCE_ACCEPTED,
  INSURANCE_BANK_DETAILS_UPDATED,
  INSURANCE_POLICY_PDF_GENERATED,
  INSURANCE_TERMINATED,
  INSURANCE_VARIATION_UPDATED,
} from '../../wsEventTypes';
import { PdfStatusEnum } from '../Invoices/InvoiceList.vue';

const reasonMap = {
  1: '@insurance/expensiveTerminationReasonLabel',
  2: '@insurance/noNeedTerminationReasonLabel',
  3: '@insurance/noLoanTerminationReasonLabel',
  4: '@insurance/hasIncomeInsuranceTerminationReasonLabel',
  5: '@insurance/hasLoanProtyectionTerminationReasonLabel',
  6: '@insurance/otherTerminationReasonLabel',
  7: '@insurance/unpaidTerminationReasonLabel',
  8: '@insurance/notAcceptedTerminationReasonLabel',
  9: '@insurance/regretTerminationReasonLabel',
  10: '@insurance/maxAgeReachedTerminationReasonLabel',
};

const hiddenButtonStatuses = [5];
const productGroups = {
  TF: 1,
};

export default {
  components: {
    PremiumForm,
    BankDetailsForm,
    TerminateForm,
    InvoiceList,
    DebtList,
    TransactionList,
    CustomerList,
    AuditTrailList,
    BgcAuditTrailList,
    BalanceCorrectionForm,
    ErrorsList,
    BackgroundErrorsList,
    ManualReminderForm,
    RecommendationList,
    WinbackForm,
    WinbackList,
    ContractAddCommentDialog,
    CampaignsList,
    RepaymentForm,
  },

  props: {
    insetFab: String,
  },

  data() {
    return {
      getMandateCommentCode,
      getMandateInformationCode,
      premiumDialog: false,
      bankDetailsDialog: false,
      shouldShowUnterminationDialog: false,
      showUnterminationFabButton: false,
      canBeUnterminated: false,
      terminateDialog: false,
      balanceCorrectionDialog: false,
      manualReminderDialog: false,
      contractLoading: false,
      winBackDialog: false,
      repaymentDialog: false,
      activeTab: 'audit-trail',
      showAddCommentDialog: false,
      contract: {
        id: null,
        internalId: this.$route.params.internalId,
        periodCost: 0,
        status: 0,
        pdfStatus: 0,
        balance: 0,
        created: null,
        comments: [],

        product: {
          id: null,
          name: '',
          supportsLoweringPremium: false,
          supportsRaisingPremium: false,
          productGroupId: 0,
        },

        bankDetails: {
          bankName: '',
          bankNumber: '',
          clearingNumber: '',
          id: '',
          created: '',
          updated: '',
        },

        bankDetailsStatus: {
          bgcCommentCode: null,
          bgcInformationCode: null,
          created: '',
          updated: '',
        },

        offer: {
          acceptedAt: '',
          acceptedBy: '',
        },

        user: {
          id: '',
          fullName: '',
        },

        productVariation: {
          coveredAmount: '0',
          id: null,
        },

        customers: [],
        corrections: [],
        termination: {
          comment: '',
          date: '',
          pdfStatus: 0,
          reason: 0,
        },
        isEnsured: false,
        onDirectDebit: false,
      },
      backgroundErrors: [],
      errorMessage: '',
      errorMessageUnterminate: '',
      invoiceIds: [],

      hasActiveCampaign: false,

      productGroups,

      lastPremiumChange: null,
    };
  },

  computed: {
    ...mapGetters(['isCompanyAgent']),

    acceptedAt() {
      return this.contract.offer.acceptedAt
        ? defaultDateTimeFormatter(this.contract.offer.acceptedAt)
        : '';
    },

    premiumStartDate() {
      if (!this.contract.offer.acceptedAt) {
        return '-';
      }

      let date = moment(this.contract.created);

      if (this.contract.premiumStartDate) {
        date = moment(this.contract.premiumStartDate);
      }

      return isoDateFormatter(date);
    },

    inabilityToWorkCoverageStartDate() {
      if (!this.contract.offer.acceptedAt) {
        return '-';
      }

      let date = moment(this.contract.created).add(30, 'days');

      if (this.contract.premiumStartDate) {
        date = moment(this.contract.premiumStartDate).add(30, 'days');
      }

      return isoDateFormatter(date);
    },

    involuntaryUnemploymentCoverageStartDate() {
      if (!this.contract.offer.acceptedAt) {
        return '-';
      }

      let date = moment(this.contract.offer.created).add(90, 'days');

      if (this.contract.premiumStartDate) {
        date = moment(this.contract.premiumStartDate).add(90, 'days');
      }

      return isoDateFormatter(date);
    },

    lastAdjustmentBy() {
      if (this.contract.product.productGroupId != productGroups.TF) {
        return this.contract.user.fullName;
      }

      if (this.lastPremiumChange) {
        return `${this.lastPremiumChange.byEmployee.firstName} ${
          this.lastPremiumChange.byEmployee.lastName
        }`;
      }

      return this.contract.user.fullName;
    },

    isTerminated() {
      return (
        this.contract.status == ContractStatusEnum.terminated ||
        this.contract.status == ContractStatusEnum.terminating
      );
    },

    isActive() {
      return this.contract.status == ContractStatusEnum.offerAccepted;
    },

    contractStatus() {
      return contractStatusLabel[this.contract.status]
        ? this.$t(contractStatusLabel[this.contract.status])
        : this.contract.status;
    },

    hideFinancialOperationBtn() {
      return this.contract.status === ContractStatusEnum.created;
    },

    statusBankDetailsMessage() {
      const classes = ['align-end'];
      if (Number(this.contract.bankDetails.status) === 4) {
        classes.push('status-message');
      }
      return classes.join(' ');
    },

    contractStatusMessage() {
      const classes = ['align-end'];
      if (
        Number(this.contract.status) === ContractStatusEnum.terminated ||
        this.contract.status == ContractStatusEnum.terminating
      ) {
        classes.push('status-message');
      }
      return classes.join(' ');
    },

    bankDetailsStatus() {
      return this.contract.bankDetails && bankDetailsStatus[this.contract.bankDetails.status]
        ? this.$t(bankDetailsStatus[this.contract.bankDetails.status])
        : this.$t('@insurance/bankDetails/absentStatusLabel');
    },

    bankDetailsWereUpdated() {
      return (
        this.contract.bankDetails &&
        this.contract.bankDetails.created &&
        this.contract.bankDetails.updated &&
        moment(this.contract.bankDetails.updated).isAfter(this.contract.bankDetails.created)
      );
    },

    bankDetailsFormattedDate() {
      if (!this.contract.bankDetails) {
        return '';
      }

      const d = this.bankDetailsWereUpdated
        ? this.contract.bankDetails.updated
        : this.contract.bankDetails.created;

      return defaultDateTimeFormatter(d);
    },

    bankDetailsStatusWasUpdated() {
      return (
        this.contract.bankDetailsStatus &&
        this.contract.bankDetailsStatus.created &&
        this.contract.bankDetailsStatus.updated &&
        moment(this.contract.bankDetailsStatus.updated).isAfter(
          this.contract.bankDetailsStatus.created,
        )
      );
    },

    bankDetailsStatusFormattedDate() {
      if (!this.contract.bankDetailsStatus.id) {
        return '';
      }

      const d = this.bankDetailsStatusWasUpdated
        ? this.contract.bankDetailsStatus.updated
        : this.contract.bankDetailsStatus.created;

      return defaultDateTimeFormatter(d);
    },

    coveredAmountDisplay() {
      return this.contract.product.productGroupId === productGroups.TF &&
        this.contract.productVariation
        ? this.contract.productVariation.coveredAmount + 'kr'
        : '-';
    },

    currentBalance() {
      return this.contract.corrections.length
        ? `${this.contract.balance} (pending)`
        : this.contract.balance;
    },
    contractCreatedAt() {
      return this.contract.created ? defaultDateTimeFormatter(this.contract.created) : '';
    },
    offerCreatedAt() {
      return this.contract.offer.created
        ? defaultDateTimeFormatter(this.contract.offer.created)
        : '';
    },
    createdBy() {
      if (this.contract.user && this.contract.user.id !== 1) {
        return this.contract.user.fullName;
      }
      return 'Customer';
    },

    createdSource() {
      if (this.contract.source) {
        return this.translateInsuranceSource(this.contract.source);
      }

      return '-';
    },

    pdfReady() {
      return this.contract.pdfStatus === PdfStatusEnum.generated;
    },

    showActionsFabButtons() {
      // Is loaded and has relevant status
      return (
        !!this.contract.id &&
        !hiddenButtonStatuses.includes(this.contract.status) &&
        !this.isCompanyAgent
      );
    },

    terminationReason() {
      return reasonMap[this.contract.termination.reason]
        ? this.$t(reasonMap[this.contract.termination.reason])
        : '';
    },

    terminationDate() {
      return this.isTerminated ? defaultDateTimeFormatter(this.contract.termination.date) : '';
    },

    terminatedBy() {
      if (!this.isTerminated || !this.contract.termination) {
        return '';
      }
      const { termination } = this.contract;

      return termination.byEmployee
        ? `${termination.byEmployee.firstName} ${termination.byEmployee.lastName}`
        : termination.byCustomer
        ? `${termination.byCustomer.firstName} ${termination.byCustomer.lastName}`
        : this.$t('@auditTrailEventsList/systemUserName');
    },

    showFabTooltips() {
      return !this.insetFab;
    },

    bankDetailsAddedBy() {
      if (!this.contract.bankDetails.id) {
        return '—';
      }
      return this.contract.bankDetails.addedByEmployee
        ? this.contract.bankDetails.addedByEmployee.full_name
        : 'Kund';
    },
  },

  methods: {
    defaultDateTimeFormatter,
    async fetchComments() {
      const { data: comments } = await this.$http.get(
        `/api/insurances/${this.contract.id}/comments?skip=0&take=50`,
      );
      this.contract.comments = comments.data;
    },

    async fetchLastPremiumChangeInformation() {
      const { data: lastPremiumChange } = await this.$http.get(
        `/api/insurances/${this.contract.id}/last-premium-change`,
      );

      this.lastPremiumChange = lastPremiumChange;
    },

    async onAddComment(comment) {
      const customerContactPerson = this.contract.customers.find(
        (customer) => customer.isContactPerson,
      );

      if (!customerContactPerson) {
        return;
      }

      try {
        await this.$http.post(`/api/insurances/${this.contract.id}/comments`, {
          customerId: customerContactPerson.id,
          comment,
        });

        void this.fetchComments();
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.showAddCommentDialog = false;
      }
    },

    async fetchContract() {
      const {
        data: { data: contract, errors },
      } = await this.$http.get(`/api/insurances/${this.contract.internalId}`);

      let status = contract.status;

      if (
        contract.termination &&
        contract.termination.date &&
        contract.status !== ContractStatusEnum.terminated
      ) {
        status = ContractStatusEnum.terminating;
      }

      this.contract = { ...this.contract, ...contract, status };
      this.backgroundErrors = errors;
    },

    async fetchBalance() {
      if (!this.contract.id) {
        await this.fetchContract();
      }
      const { data: invoiceCorrections = [] } = await this.$http.get(
        `/api/balance-corrections?contractId=${
          this.contract.id
        }&from=1970-01-01T00:00:00.000Z&take=9999&skip=0`,
      );
      const { data: balances } = await this.$http.get(
        `/api/balances?contractId=${
          this.contract.id
        }&isContractBalance=true&isActive=true&take=9999&skip=0`,
      );

      const balance = balances.length ? balances[0].balance : 0;
      const corrections = invoiceCorrections.filter((el) => el.status === 'new');

      this.contract = { ...this.contract, balance, corrections };
    },

    async fetchAll() {
      try {
        this.contractLoading = true;
        await this.fetchContract();
        await this.fetchBalance();
        await this.fetchHasActiveCampaign();
        await this.fetchLastPremiumChangeInformation();
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.contractLoading = false;
      }
    },

    async fetchHasActiveCampaign() {
      this.hasActiveCampaign = false;

      try {
        const { data: response } = await this.$http.get(
          `/api/insurance/${this.contract.id}/campaigns/has-active-campaign`,
        );

        this.hasActiveCampaign = response.active;
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      }
    },

    onCustomerClick(applicant) {
      if (applicant.id) {
        this.$router.push(`/customers/${applicant.id}`);
      }
    },

    fetchedInvoices(invoices) {
      this.invoiceIds = invoices;
    },

    translateInsuranceSource(source) {
      const sources = {
        PORTAL_WITH_ACCEPT: this.$t('@insurance/sources/PORTAL_WITH_ACCEPT'),
        PORTAL_STANDALONE: this.$t('@insurance/sources/PORTAL_STANDALONE'),
        WEBSITE: this.$t('@insurance/sources/WEBSITE'),
        PRIVATE_LOAN_IFRAME: this.$t('@insurance/sources/PRIVATE_LOAN_IFRAME'),
        INSURANCE_SYSTEM: this.$t('@insurance/sources/INSURANCE_SYSTEM'),
      };

      return sources[source] || '-';
    },

    async canUnterminate() {
      if (this.contract.status !== ContractStatusEnum.terminated) {
        this.showUnterminationFabButton = false;
        return;
      }

      try {
        const { data } = await this.$http.get(`/api/insurances/${this.contract.id}/unterminate`);

        this.canBeUnterminated = data.canUnterminate;
        this.errorMessageUnterminate = data.reason;
      } catch (e) {
        this.errorMessageUnterminate = getErrorMessage(e);
      } finally {
        this.showUnterminationFabButton = true;
      }
    },

    async tryUnterminate() {
      if (!this.canBeUnterminated) {
        return;
      }

      try {
        await this.$http.post(`/api/insurances/${this.contract.id}/unterminate`);
        this.shouldShowUnterminationDialog = false;
        this.showUnterminationFabButton = false;
        this.fetchAll();
      } catch (e) {
        this.errorMessageUnterminate = getErrorMessage(e);
      }
    },

    onClickShowUnterminationDialog() {
      this.shouldShowUnterminationDialog = true;
      this.fab = false;
    },
  },

  sockets: {
    insuranceSubscribe: function(data) {
      const eventTypes = [
        INSURANCE_ACCEPTED,
        INSURANCE_TERMINATED,
        INSURANCE_VARIATION_UPDATED,
        INSURANCE_BANK_DETAILS_UPDATED,
        INSURANCE_POLICY_PDF_GENERATED,
        BALANCE_CORRECTION_CREATED,
        CONTACT_PERSON_CHANGED,
      ];
      if (
        data.meta &&
        data.meta.contractId === this.contract.id &&
        eventTypes.includes(data.type)
      ) {
        switch (data.type) {
          case CONTACT_PERSON_CHANGED:
          case INSURANCE_ACCEPTED:
          case INSURANCE_TERMINATED:
          case INSURANCE_VARIATION_UPDATED:
          case INSURANCE_POLICY_PDF_GENERATED:
            this.fetchContract();
            break;
          default:
            this.fetchAll();
            break;
        }
      }
    },
  },

  async created() {
    await this.fetchAll();
    await this.fetchComments();
    this.$store.commit('updateContractStatus', this.contract.status);
    await this.canUnterminate();
  },

  async beforeRouteUpdate(to, from, next) {
    this.contract.internalId = to.params.internalId;
    this.fetchAll();
    next();
  },

  beforeDestroy() {
    this.$store.commit('updateContractStatus', null);
    this.invoiceIds = [];
  },
};
</script>

<style scoped>
.status-message {
  color: red;
}

.hoverable {
  text-decoration: underline;
  cursor: pointer;
}
.action-btn button {
  max-width: 40px;
}
.action-btn .tooltip {
  background: #616161;
  border-radius: 2px;
  opacity: 0.9;
  color: #fff;
  font-size: 12px;
  display: inline-block;
  padding: 5px 8px;
  width: auto;
  white-space: nowrap;
  margin-left: 5px;
}
.v-list__tile__content.align-end {
  align-items: flex-end;
}

.timeline {
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) calc(50% - 1px),
    rgba(192, 192, 192, 1) calc(50%),
    rgba(0, 0, 0, 0) calc(50% + 1px)
  );
}
.timeline-box {
  background: white;
  border: 1px solid #ccc;
}
.timeline-text {
  font-size: 13px;
}
.comments-table tbody tr td {
  padding-bottom: 25px;
}
</style>
